import { z } from 'zod'
import { oc } from '@orpc/contract'
import { US_STATE_CODES } from '../../practitioner/handlers/member-credentials.contract'
import {
  INSURANCE_CATEGORIES,
  INSURANCE_CATEGORY_SYSTEM,
  INSURANCE_PLAN_TYPE_SYSTEM,
  INSURANCE_PLAN_TYPES,
  PLAN_TYPES_BY_CATEGORY,
  planTypesFor,
  type InsuranceCategory,
  type InsurancePlanType,
} from '../../model/insurance-plan.schema'

// Identifier systems for platform-clinic scheduling agreements. All routing
// facts live in Contract.identifier because Contract.type is not a searchable
// field in this Medplum deployment; Contract.type is kept for readability only.
export const PLATFORM_ORG_SYSTEM = 'https://behuman.health/fhir/platform-organization'
export const PLATFORM_ORG_VALUE = 'behuman-platform'
export const CONTRACT_KIND_SYSTEM = 'https://behuman.health/fhir/contract-kind'
export const PLATFORM_CLINIC_AGREEMENT = 'platform-clinic-agreement'
export const SERVICE_STATE_SYSTEM = 'https://behuman.health/fhir/service-state'
export const SERVICE_SCOPE_SYSTEM = 'https://behuman.health/fhir/service-scope'
export const SERVICE_SCOPE_NATIONAL = 'national'

// The category/plan-type vocabulary lives in the shared model (the Stedi eligibility
// mapper classifies against the same six values); re-exported so this contract stays the
// import point for the agreements feature.
export {
  INSURANCE_CATEGORIES,
  INSURANCE_CATEGORY_SYSTEM,
  INSURANCE_PLAN_TYPE_SYSTEM,
  INSURANCE_PLAN_TYPES,
  PLAN_TYPES_BY_CATEGORY,
  planTypesFor,
  type InsuranceCategory,
  type InsurancePlanType,
}

const insuranceCategoryEnum = z.enum(['medicare', 'commercial', 'medicaid'], {
  error: 'Select an insurance category',
})

export const createOrganizationAgreementInputSchema = z
  .object({
    organizationId: z.string().min(1),
    insuranceCategory: insuranceCategoryEnum,
    insurancePlanType: z.enum(INSURANCE_PLAN_TYPES, { error: 'Select a plan type' }),
    national: z.boolean(),
    states: z.array(z.enum(US_STATE_CODES)).default([]),
  })
  .superRefine((data, ctx) => {
    if (!planTypesFor(data.insuranceCategory).includes(data.insurancePlanType)) {
      ctx.addIssue({
        code: 'custom',
        path: ['insurancePlanType'],
        message: `${data.insurancePlanType} is not a valid plan type for ${data.insuranceCategory}`,
      })
    }
    // National scope and explicit states are mutually exclusive; exactly one is required.
    if (data.national && data.states.length > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['states'],
        message: 'National coverage and state selection are mutually exclusive',
      })
    }
    if (!data.national && data.states.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['states'],
        message: 'Select at least one state or national coverage',
      })
    }
    if (new Set(data.states).size !== data.states.length) {
      ctx.addIssue({ code: 'custom', path: ['states'], message: 'Duplicate states' })
    }
  })

export type CreateOrganizationAgreementInput = z.infer<
  typeof createOrganizationAgreementInputSchema
>

// Boundary row shape (never a FHIR resource). `status === 'executed'` means
// the agreement is active for scheduling; anything else is terminated history.
export const agreementRowSchema = z.object({
  contractId: z.string(),
  insuranceCategory: z.string(),
  insurancePlanType: z.string(),
  national: z.boolean(),
  states: z.array(z.string()),
  status: z.string(),
})

export type AgreementRow = z.infer<typeof agreementRowSchema>

export const listOrganizationAgreements = oc
  .route({ method: 'GET', path: '/v2/super/organizations/{organizationId}/agreements' })
  .input(z.object({ organizationId: z.string().min(1) }))
  .output(z.object({ agreements: z.array(agreementRowSchema) }))

export const createOrganizationAgreement = oc
  .route({ method: 'POST', path: '/v2/super/organizations/{organizationId}/agreements' })
  .input(createOrganizationAgreementInputSchema)
  .output(agreementRowSchema)

export const terminateOrganizationAgreement = oc
  .route({
    method: 'POST',
    path: '/v2/super/organizations/{organizationId}/agreements/{contractId}/terminate',
  })
  .input(z.object({ organizationId: z.string().min(1), contractId: z.string().min(1) }))
  .output(agreementRowSchema)
